import {Button, DataStatus, Flag, Separator} from 'platform/components';
import {Box, Hide, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isNotNil} from 'ramda';

import {VehicleMotWarningResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {isLastIndexInArray, suffixTestId, TestIdProps} from 'shared/web';

import {VehicleMotCategory} from '../../utils/getVehicleMotDataQuery';
import {VehicleMotRowLayout} from './types/VehicleMotRowLayout';
import {VehicleMotWarningRow} from './VehicleMotWarningRow';

interface VehicleMotSectionProps extends TestIdProps {
  category: VehicleMotCategory;
  count: number | undefined;
  rows: VehicleMotWarningResponseBody[];
  rowLimit: number;
  layout: VehicleMotRowLayout;
  isAllBranches: boolean;
  isViewLoading: boolean;
  canViewAll: boolean;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  isProbe: boolean;
  onRetry: () => void;
  onViewAll: (category: VehicleMotCategory) => void;
}

// Inline rows are bare 21px link lines, so the section spaces them itself: a 4px gap on each side
// of the 1px separator keeps the 8px rhythm between two rows without padding the outer edges.
const INLINE_ROW_SPACING = 1;
const STACKED_ROW_SPACING = 2;

// The inline header sits directly above tightly packed rows, so a 4px gap is enough to separate it
// and buys back the height the wide tile needs for a fifth row per column.
const INLINE_SECTION_SPACING = 1;
const STACKED_SECTION_SPACING = 2;

// The translated titles end with a colon because the stacked layout puts the count on the next
// line. The inline header reads as a heading followed by a flag, so the colon has to go.
const stripTrailingColon = (title: string) => title.replace(/:$/, '');

export function VehicleMotSection(props: VehicleMotSectionProps) {
  const title =
    props.category === 'expired'
      ? i18n.t('page.homepage.vehicleWarehouse.labels.motExpired')
      : i18n.t('page.homepage.vehicleWarehouse.labels.motExpiresIn30Days');
  const visibleRows = props.rows.slice(0, props.rowLimit);
  const carCount = i18n.t('page.homepage.vehicleWarehouse.labels.carCount', {count: props.count});
  // A section without cars has nothing to shout about, so its flag drops the severity colour.
  const countColorScheme = match({category: props.category, isEmpty: props.count === 0})
    .with({isEmpty: true}, () => 'neutral' as const)
    .with({category: 'expired'}, () => 'red' as const)
    .with({category: 'expiring'}, () => 'orange' as const)
    .exhaustive();

  const countText = (
    <Show when={isNotNil(props.count)}>
      <Text size="xSmall" color="secondary" data-testid={suffixTestId('count', props)}>
        {carCount}
      </Text>
    </Show>
  );
  const countFlag = (
    <Show when={isNotNil(props.count)}>
      <Flag
        label={carCount}
        size="small"
        isSubtle
        colorScheme={countColorScheme}
        data-testid={suffixTestId('count', props)}
      />
    </Show>
  );
  const viewAllButton = (
    <Show when={props.canViewAll}>
      <Button
        title={i18n.t('general.actions.viewAll')}
        variant="link"
        size="small"
        isDisabled={props.isViewLoading || props.count === 0}
        onClick={() => props.onViewAll(props.category)}
        data-testid={suffixTestId('viewAll', props)}
      />
    </Show>
  );

  return (
    <VStack
      spacing={props.layout === 'inline' ? INLINE_SECTION_SPACING : STACKED_SECTION_SPACING}
      data-testid={props['data-testid']}
    >
      {match(props.layout)
        .with('inline', () => (
          <HStack spacing={2} align="center" justify="space-between">
            <HStack spacing={2} align="center" minWidth={0}>
              <Box minWidth={0}>
                <Text alternative noWrap>
                  {stripTrailingColon(title)}
                </Text>
              </Box>
              {countFlag}
            </HStack>
            {viewAllButton}
          </HStack>
        ))
        .with('stacked', () => (
          <Fragment>
            <HStack spacing={2} align="center" justify="space-between">
              <Text alternative>{title}</Text>
              {viewAllButton}
            </HStack>
            {countText}
          </Fragment>
        ))
        .exhaustive()}
      <DataStatus
        minHeight={8}
        isLoading={props.isPreviewLoading}
        isError={props.isPreviewError}
        isEmpty={visibleRows.length === 0}
        emptyMessage={i18n.t('general.labels.noData')}
        errorAction={{
          title: i18n.t('general.actions.retry'),
          onClick: props.onRetry,
          variant: 'secondary',
        }}
      >
        <VStack spacing={props.layout === 'inline' ? INLINE_ROW_SPACING : STACKED_ROW_SPACING}>
          {visibleRows.map((vehicle, index) => (
            <Fragment key={vehicle.id}>
              <VehicleMotWarningRow
                vehicle={vehicle}
                category={props.category}
                layout={props.layout}
                rowIndex={index}
                showBranch={props.isAllBranches}
                isProbe={props.isProbe}
              />
              <Hide when={isLastIndexInArray(visibleRows, index)}>
                <Separator spacing={0} />
              </Hide>
            </Fragment>
          ))}
        </VStack>
      </DataStatus>
    </VStack>
  );
}
